import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { VfxGradientAtlas } from "../textures/vfx_gradient_atlas.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";

import { AttributeSize } from "../types/core_types.js";

export const BallisticStreakLayout = {
  aPosGrad: AttributeSize.Vec4,      // x, y, z, gradient_row
  aVelGravity: AttributeSize.Vec4,   // vx, vy, vz, gravity
  aSizeStretch: AttributeSize.Vec4,  // width, height, stretch_factor, floor_y
  aTimeParams: AttributeSize.Vec4,   // start_time, duration, shrink_start, unused
  aUvBounds: AttributeSize.Vec4,     // min_x, min_y, max_x, max_y
} as const;

export class VfxBallisticStreakBatch extends InstanceBatch<typeof BallisticStreakLayout>
{
  private static readonly MAX_INSTANCES  : number = 150;
  private static readonly RENDER_ORDER   : number = 30;

  constructor()
  {
    const base_geometry = new THREE.PlaneGeometry( 1, 1 );
    
    let atlas_texture: THREE.Texture | null = null;
    let gradient_texture: THREE.Texture | null = null;
    try { atlas_texture = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}
    try { gradient_texture = VfxGradientAtlas.getTexture(); } catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTime:            { value: 0.0 },
        uTexture:         { value: atlas_texture },
        uGradientTexture: { value: gradient_texture },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosGrad;
        attribute vec4 aVelGravity;
        attribute vec4 aSizeStretch;
        attribute vec4 aTimeParams;
        attribute vec4 aUvBounds;

        varying vec2 vUv;
        varying float vGradientY;
        varying float vProgress;
        varying float vAlphaMultiplier;

        void main() {
          vUv = mix( aUvBounds.xy, aUvBounds.zw, uv );
          vGradientY = aPosGrad.w;
          
          float start_time = aTimeParams.x;
          float duration = aTimeParams.y;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );
          vProgress = progress;
          
          float shrink_start = aTimeParams.z;
          float scale_multiplier = 1.0;
          if ( progress > shrink_start ) {
            scale_multiplier = 1.0 - clamp( ( progress - shrink_start ) / max( 1.0 - shrink_start, 0.001 ), 0.0, 1.0 );
          }
          
          vec3 start_pos = aPosGrad.xyz;
          vec3 velocity = aVelGravity.xyz;
          float gravity = aVelGravity.w;
          vec2 size = aSizeStretch.xy;
          float stretch_factor = aSizeStretch.z;
          float floor_y = aSizeStretch.w;
          
          vec3 computed_pos = start_pos + velocity * age + vec3( 0.0, -0.5 * gravity * age * age, 0.0 );
          if ( floor_y > -900.0 ) {
            computed_pos.y = max( computed_pos.y, floor_y );
          }
          
          vec3 current_velocity = velocity + vec3( 0.0, -gravity * age, 0.0 );
          float speed = length( current_velocity );
          vec3 dir_y = speed > 0.001 ? ( current_velocity / speed ) : vec3( 0.0, -1.0, 0.0 );
          
          vec3 dir_z = normalize( cameraPosition - computed_pos );
          vec3 dir_x = normalize( cross( dir_y, dir_z ) );
          dir_z = normalize( cross( dir_x, dir_y ) );
          mat3 basis = mat3( dir_x, dir_y, dir_z );
          
          float stretch = ( size.y + speed * stretch_factor ) * scale_multiplier;
          vec3 scaled_pos = position * vec3( size.x * scale_multiplier, stretch, 1.0 );
          vec3 rotated_pos = basis * scaled_pos;
          
          vec4 mvPosition = modelViewMatrix * vec4( computed_pos, 1.0 );
          mvPosition.xyz += rotated_pos;
          gl_Position = projectionMatrix * mvPosition;
          
          // Fast fade-in to prevent popping
          vAlphaMultiplier = clamp( progress / 0.05, 0.0, 1.0 );
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform sampler2D uGradientTexture;
        
        varying vec2 vUv;
        varying float vGradientY;
        varying float vProgress;
        varying float vAlphaMultiplier;

        void main() {
          vec4 spark_sample = texture2D( uTexture, vUv );
          vec4 gradient_val = texture2D( uGradientTexture, vec2( vProgress, vGradientY ) );
          
          vec3 final_color = gradient_val.rgb * spark_sample.rgb;
          float final_alpha = spark_sample.a * gradient_val.a * vAlphaMultiplier;
          
          gl_FragColor = vec4( final_color, final_alpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxBallisticStreakBatch.MAX_INSTANCES, layout: BallisticStreakLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxBallisticStreakBatch.RENDER_ORDER;
  }

  public spawnParticle(
    res_id: ResourceID,
    gradient_row: number,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    gravity: number,
    size: THREE.Vector2,
    stretch_factor: number,
    fade_start: number, // renamed to shrink_start logically, passed to aTimeParams.z
    start_time: number,
    duration: number,
    floor_y: number = -999.0
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uTexture.value )
      try { mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}
    if ( !mat.uniforms.uGradientTexture.value )
      try { mat.uniforms.uGradientTexture.value = VfxGradientAtlas.getTexture(); } catch ( _e ) {}

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const bounds = Vfx3DGlobalAtlas.getUVRect( res_id );

    const pos_grad_attr      = this.geometry.getAttribute( "aPosGrad" ).array as Float32Array;
    const vel_gravity_attr   = this.geometry.getAttribute( "aVelGravity" ).array as Float32Array;
    const size_stretch_attr  = this.geometry.getAttribute( "aSizeStretch" ).array as Float32Array;
    const time_params_attr   = this.geometry.getAttribute( "aTimeParams" ).array as Float32Array;
    const uv_bounds_attr     = this.geometry.getAttribute( "aUvBounds" ).array as Float32Array;

    pos_grad_attr[ idx * 4 ]     = start_pos.x;
    pos_grad_attr[ idx * 4 + 1 ] = start_pos.y;
    pos_grad_attr[ idx * 4 + 2 ] = start_pos.z;
    pos_grad_attr[ idx * 4 + 3 ] = gradient_row;

    vel_gravity_attr[ idx * 4 ]     = velocity.x;
    vel_gravity_attr[ idx * 4 + 1 ] = velocity.y;
    vel_gravity_attr[ idx * 4 + 2 ] = velocity.z;
    vel_gravity_attr[ idx * 4 + 3 ] = gravity;

    size_stretch_attr[ idx * 4 ]     = size.x;
    size_stretch_attr[ idx * 4 + 1 ] = size.y;
    size_stretch_attr[ idx * 4 + 2 ] = stretch_factor;
    size_stretch_attr[ idx * 4 + 3 ] = floor_y;

    time_params_attr[ idx * 4 ]     = start_time;
    time_params_attr[ idx * 4 + 1 ] = duration;
    time_params_attr[ idx * 4 + 2 ] = fade_start;
    time_params_attr[ idx * 4 + 3 ] = 0.0;

    uv_bounds_attr[ idx * 4 ]     = bounds.min_x;
    uv_bounds_attr[ idx * 4 + 1 ] = bounds.min_y;
    uv_bounds_attr[ idx * 4 + 2 ] = bounds.max_x;
    uv_bounds_attr[ idx * 4 + 3 ] = bounds.max_y;

    this.geometry.getAttribute( "aPosGrad" ).needsUpdate     = true;
    this.geometry.getAttribute( "aVelGravity" ).needsUpdate  = true;
    this.geometry.getAttribute( "aSizeStretch" ).needsUpdate = true;
    this.geometry.getAttribute( "aTimeParams" ).needsUpdate  = true;
    this.geometry.getAttribute( "aUvBounds" ).needsUpdate    = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
